import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useModuleStore } from '../../stores/moduleStore'
import { BookOpenIcon, ClockIcon, AcademicCapIcon, CheckCircleIcon } from '@heroicons/react/24/outline'
import { CheckCircleIcon as CheckCircleIconSolid } from '@heroicons/react/24/solid'

export default function ModuleList() {
  const { modules, loading, error, fetchModules } = useModuleStore()

  useEffect(() => {
    fetchModules()
  }, [fetchModules])

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-gray-600">Loading modules...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        {error}
      </div>
    )
  }

  const getDifficultyColor = (level) => {
    switch (level) {
      case 'beginner':
        return 'bg-green-100 text-green-800'
      case 'intermediate':
        return 'bg-yellow-100 text-yellow-800'
      case 'advanced':
        return 'bg-red-100 text-red-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-cte-black mb-2">Course Modules</h1>
        <p className="text-gray-600">
          Master AI prompting and agent systems through {modules.length} comprehensive modules
        </p>
      </div>

      {modules.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <BookOpenIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">No modules available yet</p>
        </div>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {modules.map((module) => {
            const readingPercentage = module.user_progress?.metadata?.reading_percentage || 0
            const isCompleted = module.user_progress?.status === 'completed'
            const hasStartedReading = readingPercentage > 0 && !isCompleted

            return (
              <Link
                key={module.id}
                to={`/modules/${module.id}`}
                className="block bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md hover:border-cte-blue transition-all duration-200 relative"
              >
                {/* Completion Badge */}
                {isCompleted && (
                  <div className="absolute top-4 right-4 z-10">
                    <div className="bg-green-500 text-white rounded-full p-1">
                      <CheckCircleIconSolid className="w-5 h-5" />
                    </div>
                  </div>
                )}

                {/* Resume Reading Badge */}
                {hasStartedReading && (
                  <div className="absolute top-4 right-4 z-10">
                    <div className="bg-blue-500 text-white text-xs font-semibold px-3 py-1 rounded-full flex items-center space-x-1">
                      <BookOpenIcon className="w-3 h-3" />
                      <span>{readingPercentage}%</span>
                    </div>
                  </div>
                )}

                <div className="p-6">
                {/* Module Code */}
                <div className="text-sm font-mono text-cte-blue mb-2">
                  {module.code}
                </div>

                {/* Module Title */}
                <h3 className="text-lg font-semibold text-cte-black mb-3 line-clamp-2">
                  {module.title}
                </h3>

                {/* Description */}
                <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                  {module.description}
                </p>

                {/* Meta Info */}
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center text-gray-500">
                    <ClockIcon className="w-4 h-4 mr-1" />
                    <span>{module.estimated_hours}h</span>
                  </div>

                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(module.difficulty_level)}`}>
                    {module.difficulty_level}
                  </span>
                </div>

                {/* Learning Objectives Count */}
                {module.learning_objectives && module.learning_objectives.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <div className="flex items-center text-sm text-gray-500">
                      <AcademicCapIcon className="w-4 h-4 mr-1" />
                      <span>{module.learning_objectives.length} learning objectives</span>
                    </div>
                  </div>
                )}

                {/* Reading Progress Bar */}
                {hasStartedReading && (
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600 font-medium">Reading Progress</span>
                        <span className="text-blue-600 font-semibold">{readingPercentage}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-blue-500 to-blue-600 transition-all duration-300"
                          style={{ width: `${readingPercentage}%` }}
                        />
                      </div>
                      <p className="text-xs text-gray-500">Continue reading</p>
                    </div>
                  </div>
                )}
              </div>
            </Link>
            )
          })}
        </div>
      )}
    </div>
  )
}

